import { Accordion, AccordionItem } from "@heroui/accordion";
import { Button } from "@heroui/button";
import {
  Navbar as HeroUINavbar,
  NavbarBrand,
  NavbarContent,
  NavbarItem,
  NavbarMenu,
  NavbarMenuToggle,
} from "@heroui/navbar";
import { link as linkStyles } from "@heroui/theme";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation, useNavigate } from "react-router-dom";

import ProductNav from "./product-nav";
import ServiceNavCard from "./service-nav-card";

import { services } from "@/assets/data/navbar";
import AiStarIcon from "@/assets/icons/AiStarIcon";
import DownArrowIcon from "@/assets/icons/DownArrowIcon";
import LOGO from "@/assets/images/logo.svg";
import Site from "@/config/site";
import useScreenSize from "@/hooks/useScreenSize";
import { cn } from "@/lib/utils";

export const Navbar = () => {
  const isMdScreen = useScreenSize("md");
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [openDropdownIndex, setOpenDropdownIndex] = useState<number | null>(
    null,
  );
  const navListRef = useRef<HTMLDivElement | null>(null);

  // Close dropdown when route changes (e.g. navigating from a detail page)
  useEffect(() => {
    setOpenDropdownIndex(null);
  }, [location.pathname]);

  // Close dropdown when clicking outside nav bar AND outside any open portal dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const insideNavList = navListRef.current?.contains(target);
      const insidePortalDropdown = !!(target as Element).closest?.(
        ".nav-dropdown-enter",
      );

      if (!insideNavList && !insidePortalDropdown) {
        setOpenDropdownIndex(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 25);
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const isDropdownOpen = openDropdownIndex !== null;

  return (
    <>
      {/* Backdrop overlay – rendered outside the navbar so it covers the whole page */}
      <div
        aria-hidden="true"
        className={cn(
          "fixed inset-0 z-[40] transition-all duration-300 pointer-events-none",
          isDropdownOpen
            ? "backdrop-blur-sm bg-black/30 opacity-100 pointer-events-auto"
            : "opacity-0",
        )}
        style={{
          backdropFilter: isDropdownOpen ? "blur(6px) brightness(0.6)" : "none",
        }}
        onClick={() => setOpenDropdownIndex(null)}
      />

      <HeroUINavbar
        className={cn(
          "fixed h-[85px] bg-background/30 backdrop-blur-[5px] border-b border-white/30 rounded-t-xl transition-all z-[50]",
          location.pathname === "/" && !isMdScreen
            ? scrolled
              ? "w-full top-0 left-0 right-0"
              : "w-auto top-5 left-5 right-5"
            : "w-full top-0 left-0 right-0",
        )}
        classNames={{
          content: "container",
        }}
        maxWidth="full"
      >
        <NavbarContent className="basis-1/5 sm:basis-full" justify="start">
          <NavbarBrand className="gap-3 max-w-fit">
            <Link
              className="flex justify-start items-center gap-1 w-32"
              color="foreground"
              to="/"
            >
              <img
                alt={Site.APP_NAME}
                className="w-32 object-contain"
                src={LOGO}
              />
            </Link>
          </NavbarBrand>
        </NavbarContent>

        <NavbarContent className="hidden sm:flex" justify="center">
          {/* Wrap the entire nav list so outside-click ref covers all items + dropdowns */}
          <div ref={navListRef} className="hidden lg:flex gap-5 justify-center">
            {navList.map((item, index) => (
              <div key={item.label} className="relative">
                <NavbarItem>
                  <Link
                    className={cn(
                      linkStyles({ color: "foreground" }),
                      "text-lg font-normal inline-flex items-center gap-2.5 cursor-pointer hover:text-secondary transition-colors",
                      location.pathname === item.link && "text-secondary",
                    )}
                    to={item.link as string}
                    onClick={(e) => {
                      if (item.children) {
                        e.preventDefault();
                      }
                      setOpenDropdownIndex(
                        openDropdownIndex === index ? null : index,
                      );
                    }}
                  >
                    <span>{item.label}</span>{" "}
                    {item.children ? (
                      <span
                        className="transition-transform duration-200"
                        style={{
                          display: "inline-flex",
                          transform:
                            openDropdownIndex === index
                              ? "rotate(180deg)"
                              : "rotate(0deg)",
                        }}
                      >
                        <DownArrowIcon />
                      </span>
                    ) : null}
                  </Link>
                </NavbarItem>

                {/* Dropdown rendered via portal so fixed positioning is relative
                    to the viewport, not the navbar's backdrop-blur containing block */}
                {item.children &&
                  openDropdownIndex === index &&
                  createPortal(item.children, document.body)}
              </div>
            ))}
          </div>
        </NavbarContent>

        <NavbarContent
          className="hidden sm:flex basis-1/5 sm:basis-full"
          justify="end"
        >
          <NavbarItem className="hidden md:flex gap-5">
            <Button
              className="btn bg-transparent border-2 border-primary"
              color="primary"
              radius="full"
              onPress={() =>
                navigate(location.pathname?.includes("/ai") ? "/" : "/ai")
              }
            >
              {!location.pathname?.includes("/ai") && <AiStarIcon />}
              {location.pathname?.includes("/ai")
                ? "Back to Upscale"
                : "AI School"}
            </Button>

            <Button
              className="btn"
              color="primary"
              radius="full"
              onPress={() => navigate("/contact")}
            >
              Contact Us
            </Button>
          </NavbarItem>
        </NavbarContent>

        <NavbarContent className="sm:hidden basis-1 pl-4" justify="end">
          <NavbarMenuToggle />
        </NavbarContent>

        <NavbarMenu>
          <div className="mx-4 mt-5 flex flex-col gap-2 justify-between h-full pb-5">
            <div>
              <Accordion variant="splitted">
                {navList.map((item, index) => {
                  return (
                    <AccordionItem
                      key={index}
                      aria-label={item.label}
                      classNames={{
                        content: !!item.link ? "py-0" : "",
                        trigger: "py-2.5",
                        base: "w-full rounded text-white text-left border border-white/30 bg-transparent bg-gradient-to-r to-white/[.1] from-white/5 px-5",
                      }}
                      hideIndicator={!!item.link}
                      title={item.label}
                      onPress={() => (item.link ? navigate(item.link) : null)}
                    >
                      {item.children ? item.children : null}
                    </AccordionItem>
                  );
                })}
              </Accordion>
              <div className="p-2">
                <button
                  className="relative cursor-pointer w-full"
                  onClick={() =>
                    navigate(location.pathname === "/ai" ? "/" : "/ai")
                  }
                >
                  <div
                    className="absolute -left-[1px] -top-[1px] -right-[1px] -bottom-[1px] rounded-lg"
                    style={{
                      backgroundImage:
                        "linear-gradient(to top left, #3A8DFF, #1ABC9C, #FF6B6B, #6C4AB6)",
                    }}
                  />
                  <Button
                    fullWidth
                    className="bg-gradient-to-r to-white/[.1] from-white/5"
                    radius="sm"
                    onPress={() =>
                      navigate(location.pathname === "/ai" ? "/" : "/ai")
                    }
                  >
                    {!!(location.pathname !== "/ai") && <AiStarIcon />}
                    {location.pathname === "/ai"
                      ? "Back to Upscale"
                      : "AI School"}
                  </Button>
                </button>
              </div>
            </div>
            <div className="px-2">
              <Button
                fullWidth
                className="px-5 min-h-10 text-base font-normal"
                color="primary"
                radius="full"
                onPress={() => navigate("/contact")}
              >
                Contact Us
              </Button>
            </div>
          </div>
        </NavbarMenu>
      </HeroUINavbar>
    </>
  );
};

const navList = [
  {
    key: "home",
    label: "Home",
    link: "/",
  },
  {
    key: "products",
    label: "Products",
    children: <ProductNav />,
  },
  {
    key: "services",
    label: "Services",
    children: <ServiceNavCard />,
    list: services,
  },
  {
    key: "case-studies",
    label: "Case Studies",
    link: "/case-studies",
  },
  {
    key: "insights",
    label: "Insights",
    link: "/insights",
  },
  {
    key: "career",
    label: "Career",
    link: "/career",
  },
  // {
  //   key: "ai",
  //   label: "AI",
  //   link: "/ai",
  // },
];
